import CloseIcon from "@mui/icons-material/Close";
import { IconButton, LinearProgress, TextField } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useState } from "react";
import { isMobile } from 'react-device-detect';
import { useAuthContext } from "../Auth";
import { AutoCompleteUser } from "../components/UI/AutoCompleteUser";
import Button from "../components/UI/Button";
import { DBUser, House, ReservationStatus, RoomReservation } from "../ostertalertypes";
import StepSingleRoom from "../pages/stepper/StepSingleRoom";
import { updateReservationStatus, updateReservation } from "../utils/database";
import { dateToStr } from "../utils/dateutils";
import { handleError } from "../utils/utils";
import { httpsCallable } from "firebase/functions";
import { fireFunctions } from "../firebase-config";
import { User } from "firebase/auth";

export interface IDialogEditBooking {
  roomReservation: RoomReservation;
  house: House;
  onClose: (changed: boolean) => void;
  open: boolean;
  allowCancel?: boolean;
  onBookingChanged: (appartment: RoomReservation, index: number) => void;
  onGuestCountChanged: (guestCount: number) => void;
}

export function DialogEditBooking(props: IDialogEditBooking) {
  const [changed, setChanged] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const authCtx = useAuthContext();

  const [comment, setComment] = useState<string>("");
  // the admin inspects an incoming request in the calendar => applying the dialog accepts the booking.
  const isBookingRequest = props.roomReservation?.status === ReservationStatus.BOOKING_REQUESTED;

  const onTextFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value.trim());
    setChanged(true);
  }

  /**
   * Decides if the apply button ("Übernehmen" / "Annehmen") is disabled.
   * - While a request is running the button is always disabled.
   * - A booking request can be accepted without any modification, so the button stays enabled.
   * - Any other booking can only be applied after something was changed.
   * @returns true if the button is disabled
   */
  function isApplyDisabled(): boolean {
    return isLoading || (!changed && !isBookingRequest);
  }

  async function onClickApply(event: React.MouseEvent<HTMLButtonElement>) {
    if (props.roomReservation) {
      try {
        setIsLoading(true);
        props.roomReservation.adminComment = comment;

        // check if it is a user with email
        const data = {
          userid: props.roomReservation.userid
        };
        const getUser = httpsCallable(fireFunctions, "getUser");
        const user = await getUser(data);
        const fireBaseUser = (user.data as any).user as User;
        const action = isBookingRequest ? "angenommen" : "geändert";
        const text = fireBaseUser.email ? `Buchung erfolgreich ${action} und Aktualisierung an ${fireBaseUser.email} geschickt` : `Buchung erfolgreich ${action}`;

        await updateReservation(props.roomReservation, isBookingRequest);
        if (isBookingRequest) {
          props.roomReservation.status = ReservationStatus.ACCEPTED;
        }
        enqueueSnackbar(text, {
          variant: "success",
          persist: false,
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        setIsLoading(false);
      } catch (error) {
        handleError(isBookingRequest ? "Fehler beim Annehmen der Buchung" : "Fehler beim Ändern der Buchung", error, enqueueSnackbar, setIsLoading);
      }
    }
    props.onClose(true);
  }

  async function onClickCancel(event: React.MouseEvent<HTMLButtonElement>) {
    if (comment.length === 0) {
      enqueueSnackbar("Grund für Stornierung angeben", {
        variant: "error",
        persist: false,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return;
    }
    if (props.roomReservation) {
      try {
        setIsLoading(true);
        props.roomReservation.status = ReservationStatus.CANCELED;
        props.roomReservation.adminComment = comment;
        await updateReservationStatus(props.roomReservation, ["status", "adminComment"]);
        enqueueSnackbar("Buchung erfolgreich storniert", {
          variant: "success",
          persist: false,
          anchorOrigin: { vertical: "top", horizontal: "center" },
        });
        setIsLoading(false);
      } catch (error) {
        handleError("Fehler beim Stornieren der Buchung", error, enqueueSnackbar, setIsLoading);
      }
    }
    props.onClose(true);
  }

  function onAbort() {
    props.onClose(false);
  }

  const onBookingChanged = (appartment: RoomReservation, index: number) => {
    props.onBookingChanged(appartment, index);
    setChanged(true);
  };

  const onUserSelected = (selection: DBUser | null) => {
    if (selection) {
      props.roomReservation.user = selection;
      props.roomReservation.userid = selection.userid;
      props.onBookingChanged(props.roomReservation, 0);
      setChanged(true);
    }
  };

  return (
    <Dialog
      fullScreen={isMobile ? true : false}
      id="dialogedit"
      PaperProps={{
        sx: {
          width: "100%",
          position: "inherit",
        },
      }}
      open={props.open}
      onClose={onAbort}
    >
      <LinearProgress style={{ "visibility": isLoading ? "visible" : "hidden" }} />
      <DialogTitle className={`flex content-center justify-between`}>
        {isBookingRequest ? "Buchungsanfrage" : "Buchung"} für {props.roomReservation?.houseName} bearbeiten
        <IconButton onClick={onAbort}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <div className={`flex flex-col md:px-6 pb-6 w-full ${isMobile ? "h-full" : ""}`}>
        <AutoCompleteUser
          users={authCtx.allUsers}
          className="mb-3"
          selectedUser={props.roomReservation.user ? props.roomReservation.user : null}
          onUserSelected={onUserSelected}
        />
        <h1 className={`${isMobile ? "p-1" : ""}`}>
          {`Gebucht durch ${props.roomReservation.booker === 'user' ? "Gast" : "Hüttenwart"} am ${dateToStr(props.roomReservation.createdDate)}. `}
          {`Letzte Änderung am ${dateToStr(props.roomReservation.lastUpdate)}.`}
        </h1>
        <StepSingleRoom
          availableRooms={Array.from(props.house.rooms.values())}
          houseName={props.house.houseName}
          roomState={props.roomReservation!}
          index={0}
          onRoomUpdated={onBookingChanged}
          enableMyReservation={true} />
        <TextField
          label="Kommentar von Gast:"
          disabled={true}
          id="tfcomment"
          className="w-full mb-5 mt-5"
          multiline
          rows={4}
          defaultValue={props.roomReservation?.userComment} />

        {props.allowCancel && <TextField
          label="Kommentar von Hüttenwart / Stornierungsgrund:"
          id="tfcomment"
          className="w-full mb-5"
          multiline
          onChange={onTextFieldChange}
          rows={4}
          defaultValue={props.roomReservation?.adminComment} />}

        <div className={`${isMobile ? "mt-auto p-1" : ""} grid grid-cols-1 ${props.allowCancel ? "md:grid-cols-3" : "md:grid-cols-2"} gap-1`}>
          <Button key="btn1" className="w-full" onClick={onClickApply} disabled={isApplyDisabled()}>
            {isBookingRequest ? "Annehmen" : "Übernehmen"}
          </Button>
          {props.allowCancel && <Button key="btn2" className="w-full" onClick={onClickCancel} disabled={isLoading}>
            Stornieren
          </Button>}
          <Button key="btn3" className="w-full" onClick={onAbort}>
            Abbrechen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

